import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import moment from "moment";
import clsx from "clsx";
// Material
import { Card, CardContent, CardMedia, Typography, Box, Stack, SvgIcon, Tooltip } from "@mui/material";
//  Icons
import carIcon from "@/assets/icons/dashboard/Car.svg";
import { ReactComponent as UserIcon } from "@/assets/icons/dashboard/User.svg";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
// API
import { getCommissionCycleTopSales } from "@/Dashboard/api/queries";
import { retrieveLocationWithStorePicture } from "@Common/api/location";
// Components
import { SalespersonOfMonthLoader } from "@/Dashboard/components/DashboardLoader";
import { RootState } from "@/redux/store";
//Default image
import  defaultImage  from  "@/assets/images/widgets/DefaultLocationPicture.png";

export default function SalespersonOfMonth() {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const today = moment().toISOString() || "";

	const { data: bestEmployeeData, isPending } = useQuery({
		queryKey: ["commissionCycleTopSales", locationId],
		queryFn: () => {
			if (!locationId) {
				throw new Error("Cant fetch data without locationId");
			}
			return getCommissionCycleTopSales(locationId, today);
		},
		enabled: !!locationId,
	});

	const { data: srcImage, isPending: isLoadingImg } = useQuery({
		queryKey: ["storePictureSource", locationId],
		queryFn: async () => {
			const loc = await retrieveLocationWithStorePicture(locationId);
			return loc?.storePictures?.[0]?.pictureURL;
		},
		enabled: !!locationId,
	});

	if (isPending || isLoadingImg) {
		return <SalespersonOfMonthLoader />;
	}

	const userName = bestEmployeeData?.topSalesAssociate?.units ? bestEmployeeData?.topSalesAssociate?.fullName : "Pending";

	return (
		<Card className="relative">
			<Box
				className={clsx("absolute w-full h-[170px] overflow-hidden z-10", {
					hidden: !bestEmployeeData?.topSalesAssociate?.units,
				})}
			>
				<Box className="absolute bg-gradient-to-t w-[500px] h-[170px] rotate-[25deg] -top-[60px] left-[170px] from-transparent to-black/50" />
				<EmojiEventsIcon className="absolute !text-white top-[18px] right-[30px] !text-[32px]" />
			</Box>
			<CardMedia
				component="img"
				image={srcImage ?? defaultImage}
				className={clsx("h-[170px]", {
					"w-full": srcImage ?? defaultImage,
					"w-auto": !srcImage || !defaultImage,
				})}
			/>
			<CardContent className="bg-gradient-to-r from-primary to-gradient-blue-2 !px-[30px] !py-3">
				<Stack justifyContent="space-between" alignItems="center" direction="row">
					<Stack justifyContent="space-between" alignItems="center" direction="row" columnGap={1}>
						<SvgIcon className="!text-[40px]">
							<UserIcon />
						</SvgIcon>
						<Box>
							<Tooltip title={userName} enterDelay={500} placement="top">
								<Typography className="text-white !font-sans !font-medium !text-[20px] !-mb-px overflow-hidden whitespace-nowrap text-ellipsis max-w-[180px]">
									{userName}
								</Typography>
							</Tooltip>
							<Typography className="text-white !font-sans !text-[13.5px]">Salesman of the Month</Typography>
						</Box>
					</Stack>
					{Boolean(bestEmployeeData?.topSalesAssociate?.units) && (
						<Stack alignItems="start" direction="row" className="rounded-[18px] bg-white px-[10px]" columnGap={1}>
							<Typography className="!text-[36px] !font-montserrat !font-bold text-dark-blue">
								{bestEmployeeData?.topSalesAssociate?.units}
							</Typography>
							<Box>
								<img src={carIcon} alt="car" className="w-[31px] mt-[15px]" />
								<Typography className="!text-[11px]/[1.5] !font-montserrat !font-bold text-dark-blue">
									DELIVERIES
								</Typography>
							</Box>
						</Stack>
					)}
				</Stack>
			</CardContent>
		</Card>
	);
}
